import { Injectable, inject } from '@angular/core';
import { BaseSite, BaseSiteService, BaseStore, Product, WindowRef, isNotNullable } from '@spartacus/core';
import { CurrentProductService, ProductSchemaBuilder } from '@spartacus/storefront';
import { DateTime } from 'luxon';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { EnvironmentService } from '../../features/services/environment.service';

interface WarehouseWsDTO {
  code: string;
  name: string;
}

declare module '@spartacus/core' {
  interface BaseStore {
    warehouses: WarehouseWsDTO[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class HisenseProductSchemaBuilder extends ProductSchemaBuilder {
  private DATE_FORMAT: string = 'yyyy-MM-DD';

  private baseSiteService = inject(BaseSiteService);
  private currentProductService = inject(CurrentProductService);
  private environmentService = inject(EnvironmentService);
  private winRef = inject(WindowRef);
  private baseStore?: BaseStore;

  override build(): Observable<any> {
    return this.baseSiteService.getActive().pipe(
      filter((active) => !!active),
      switchMap((activeBaseSite) =>
        this.baseSiteService.get(activeBaseSite).pipe(
          filter(isNotNullable),
          tap((baseSite: BaseSite) => {
            this.baseStore = baseSite.baseStore;
          }),
          switchMap(() => this.buildSchema(activeBaseSite)) // Ensures baseStore is set before calling buildSchema
        )
      )
    );
  }

  private buildSchema(active: string): Observable<any> {
    return super.build().pipe(
      filter((schema) => !!schema),
      mergeMap((response: any) => {
        return this.currentProductService.getProduct().pipe(
          switchMap((product: Product | null) => {
            if (product) {
              return combineLatest(this.collectData(product, response, active)).pipe(
                map((res: {}[]) => Object.assign({}, ...res))
              );
            }
            return of({});
          })
        );
      })
    );
  }

  private collectData(product: Product, schema: any, active: string): Observable<any>[] {
    if (!product || !product.code) {
      return [];
    }

    const schemaToBuild = this.prepareSchema(product, schema, active);

    return [of(schemaToBuild)];
  }

  private prepareSchema(product: Product, schema: any, active: string): any {
    const offers = {
      ...this.setOffers(schema['offers'], schema['brand'], product),
    };

    const url = this.getCurrentLocationUrl();
    const image = this.getProductImage(product);
    const rating = this.getAggregateRating(product);
    const reviews = this.getReviews(product);

    const schemaToBuild = {
      ...schema,
      '@id': url,
      name: product.name ? product.name : '',
      image: image,
      brand: this.getBrand(schema['brand']),
      ...(this.environmentService.getCurrentEnvironment().eShop.showPrice[active]
        ? { ...(product.price ? { offers: offers } : {}) }
        : {}),
      model: product.modelMark ? product.modelMark : '',
      gtin13: product.ean ? product.ean : '',
      mpn: product.ean ? product.ean : '',
      description: product.technicalDesc ? product.technicalDesc : '',
      ...(this.environmentService.getCurrentEnvironment().reviews.show[active]
        ? { ...(product.reviews && product.reviews.length > 0 ? { review: reviews } : null) }
        : {}),
      ...(this.environmentService.getCurrentEnvironment().reviews.show[active]
        ? { ...(product.reviews && product.reviews.length > 0 ? { aggregateRating: rating } : {}) }
        : {}),
      sku: parseInt(schema['sku']),
    };

    if (!product.price || !this.environmentService.getCurrentEnvironment().eShop.showPrice[active]) {
      this.removeProperty(schemaToBuild, 'offers');
    }

    if (!product.reviews || !this.environmentService.getCurrentEnvironment().reviews.show[active]) {
      this.removeProperty(schemaToBuild, 'review');
      this.removeProperty(schemaToBuild, 'aggregateRating');
    }

    return schemaToBuild;
  }

  protected getCurrentLocationUrl(): string | undefined {
    return this.winRef.location?.href ? this.winRef.location.href.split('?')[0] : '';
  }

  private removeProperty(schema: object, prop: string) {
    delete schema[prop];
  }

  protected getProductImage(product: Product): { image?: string } {
    const image = product.images?.PRIMARY?.['zoom']?.url;
    const alt = product.images?.GALLERY?.[0]?.product.url;
    return image ? { image } : alt;
  }

  protected getAggregateRating(product: Product) {
    let rating = {};
    if (product.reviews) {
      rating = {
        ...rating,
        '@type': 'AggregateRating',
        ratingValue: product.averageRating ? product.averageRating : '',
        reviewCount: product.reviews?.length,
        bestRating: 5,
        worstRating: 1,
      };
    }
    return rating;
  }

  private getDate(number, format) {
    return DateTime.fromISO(number).toFormat(format);
  }

  protected getReviews(product: Product) {
    let review = {};
    const reviews: {}[] = [];
    const rating = this.getBaseRating();
    if (product.reviews) {
      for (let i = 0; i < product.reviews?.length; i++) {
        const date = new Date(product.reviews[i].date!).toISOString();
        const reviewRating = this.getReviewRating(product, rating, i);
        review = {
          ...review,
          '@type': 'Review',
          datePublished: this.getDate(date, this.DATE_FORMAT),
          reviewBody: product.reviews[i].comment ? product.reviews[i].comment : '',
          reviewRating: reviewRating,
        };
        if (product.reviews[i].reviewAsGuest) {
          review = {
            ...review,
            author: {
              '@type': 'Person',
              name: product.reviews[i].alias ? product.reviews[i].alias : '',
            },
          };
        } else {
          review = {
            ...review,
            author: {
              '@type': 'Person',
              name: product.reviews[i].principal ? product.reviews[i].principal?.name : '',
            },
          };
        }
        reviews.push(review);
      }
    }
    return reviews;
  }

  protected getBrand(brand: string) {
    return {
      '@type': 'Brand',
      name: brand,
    };
  }

  protected getBaseRating() {
    return {
      '@type': 'Rating',
      bestRating: 5,
      worstRating: 1,
    };
  }
  protected getReviewRating(product: Product, rating, index) {
    if (product.reviews) {
      rating = {
        ...rating,
        ratingValue: product.reviews[index].rating,
      };
    }
    return rating;
  }

  private setOffers(offers, brandName: string, product: Product) {
    offers = {
      ...offers,
      availability:
        product.stock && product.stock.stockLevel! > 0 ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock',
      url: this.getCurrentLocationUrl(),
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        brand: {
          '@type': 'Brand',
          name: brandName,
        },
      },
    };

    if (this.baseStore?.warehouses == undefined || this.baseStore?.warehouses.length == 0) {
      this.removeProperty(offers, 'availability');
    }

    return offers;
  }
}
